import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { observer } from "mobx-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import { finalize } from "rxjs";
import Permission from "src/Shared/Components/Permission/Permission";
import { useStore } from "src/Shared/Hooks";
import { ToastService } from "src/Shared/Services";
import CompanyService from "../../Services/Company.service";
import { isProspectStage } from "../../Enums/ClientStatus.enum";

/**
 * Client deletion under the CRM's existing admin gating (CR.11, decision 10):
 * once onboarding data exists the server archives instead of hard-deleting, so
 * audit history is never destroyed by a routine cleanup.
 */
const DangerZoneCard: React.FC = () => {
  const navigate = useNavigate();
  const {
    rootStore: {
      companyStore: {
        companyOverview: overview,
        getProspectsCount,
        resetCompanyList,
      },
    },
  } = useStore();

  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  // Azure-style typed-confirmation gate (task 5843, Chad's feature request): a plain Cancel/Delete
  // pair made this the only destructive action in the app one misclick away, with no "are you sure
  // you meant THIS client" check the way the SOP's own irreversible actions get.
  const [confirmText, setConfirmText] = useState<string>("");

  if (!overview) return null;

  const closeConfirm = () => {
    setConfirmOpen(false);
    setConfirmText("");
  };

  const onDelete = () => {
    setDeleting(true);
    CompanyService.deleteCompany(overview.companyRowID)
      .pipe(finalize(() => setDeleting(false)))
      .subscribe({
        next: (data) => {
          ToastService.success(data.message);
          closeConfirm();
          getProspectsCount();
          resetCompanyList();
          // Return to the grid the client belonged to.
          navigate(
            isProspectStage(overview.clientStatusID)
              ? "/company/prospects"
              : "/company",
          );
        },
        error: (error: AxiosError) => ToastService.axiosError(error),
      });
  };

  return (
    <Permission permission="CRM.Settings">
      <Paper
        elevation={0}
        sx={{ p: 2, mt: 2, border: "1px solid #f0c0c0", backgroundColor: "#fffafa" }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={600} color="error">
              Danger zone
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Deleting a client with onboarding data archives it instead, so the
              record stays available for auditing.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setConfirmOpen(true)}
          >
            Delete client
          </Button>
        </Box>
      </Paper>

      <Dialog open={confirmOpen} onClose={closeConfirm}>
        <DialogTitle>Delete {overview.companyName}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This removes “{overview.companyName}” from the grids and search. Any
            captured onboarding data goes with it. A client whose onboarding has
            started is archived rather than deleted, preserving its history for
            auditing.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            Type <strong>{overview.companyName}</strong> to confirm.
          </DialogContentText>
          <TextField
            fullWidth
            autoFocus
            size="small"
            sx={{ mt: 1 }}
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={overview.companyName}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleting || confirmText !== overview.companyName}
            onClick={onDelete}
          >
            Delete client
          </Button>
        </DialogActions>
      </Dialog>
    </Permission>
  );
};

export default observer(DangerZoneCard);
